import { SentimentType } from '@granlupo/api-client/generated/content/zeus'
import clsx from 'clsx'
import { useEffect, useState, type FunctionComponent } from 'react'
import { createFetcher } from '../../../libs/next/api/jsonApiHandler'
import type { API } from '../../../pages/api/review/[handler]'
import { Translation } from '../../contexts/TranslationContext'
import type { BlogPostReviewResult } from '../../data/BlogPostReviewFragment'
import type { ProductReviewResult } from '../../data/ProductReviewFragment'
import style from './ReviewRating.module.sass'
import { ReviewRatingItem } from './ReviewRatingItem'

type ReviewRatingProps = {
	review: ProductReviewResult | BlogPostReviewResult
	openLoginModal: () => void
}

const api = createFetcher<API>('/api/review/[handler]')

export const ReviewRating: FunctionComponent<ReviewRatingProps> = ({ review, openLoginModal }) => {
	const [userVoted, setUserVoted] = useState<SentimentType | undefined>(undefined)
	const [votedInPast, setVotedInPast] = useState(false)
	const [extraPlusPoint, setExtraPlusPoint] = useState(0)
	const [extraMinusPoint, setExtraMinusPoint] = useState(0)

	useEffect(() => {
		api('getReviewReaction', {
			id: review.id,
			type: review.__typename,
		}).then((res) => {
			if (res.reviewSentiment) {
				setUserVoted(res.reviewSentiment)
				setVotedInPast(true)
			}
		})
	}, [review.__typename, review.id])

	const incrementPlusPoints = async () => {
		try {
			if (userVoted === SentimentType.positive) {
				await api('deleteReviewReaction', {
					id: review.id,
					type: review.__typename,
				})
				setUserVoted(undefined)
				setVotedInPast(false)
				setExtraPlusPoint(extraPlusPoint - 1)
				return
			}
			if (userVoted === SentimentType.negative) {
				await api('upsertReviewReaction', {
					id: review.id,
					type: review.__typename,
					sentiment: SentimentType.positive,
				})
				setVotedInPast(false)
				setUserVoted(SentimentType.positive)
				setExtraPlusPoint(extraPlusPoint + 1)
				setExtraMinusPoint(extraMinusPoint - 1)
				return
			}
			await api('upsertReviewReaction', {
				id: review.id,
				type: review.__typename,
				sentiment: SentimentType.positive,
			})
			setUserVoted(SentimentType.positive)
			setExtraPlusPoint(extraPlusPoint + 1)
		} catch (e) {
			console.log(e)
		}
	}

	const incrementMinusPoints = async () => {
		try {
			if (userVoted === SentimentType.negative) {
				await api('deleteReviewReaction', {
					id: review.id,
					type: review.__typename,
				})
				setUserVoted(undefined)
				setVotedInPast(false)
				setExtraMinusPoint(extraMinusPoint - 1)
				return
			}
			if (userVoted === SentimentType.positive) {
				await api('upsertReviewReaction', {
					id: review.id,
					type: review.__typename,
					sentiment: SentimentType.negative,
				})
				setVotedInPast(false)
				setUserVoted(SentimentType.negative)
				setExtraMinusPoint(extraMinusPoint + 1)
				setExtraPlusPoint(extraPlusPoint - 1)
				return
			}
			await api('upsertReviewReaction', {
				id: review.id,
				type: review.__typename,
				sentiment: SentimentType.negative,
			})
			setUserVoted(SentimentType.negative)
			setExtraMinusPoint(extraMinusPoint + 1)
		} catch (e) {
			console.log(e)
		}
	}

	return (
		<>
			<div
				className={clsx(style.ratingTitle, userVoted && style.ratingTitleDisappear, votedInPast && style.notVisible)}
			>
				<Translation tr="reviewRating.useful" />
			</div>
			<div
				className={clsx(style.ratingTitleAfter, userVoted && style.ratingTitleVisible, votedInPast && style.notVisible)}
			>
				<Translation tr="reviewRating.thankYou" />
			</div>
			<div className={clsx(style.ratingContent, votedInPast && style.ratingContentOnRightSide)}>
				<ReviewRatingItem
					sentimentType={SentimentType.positive}
					review={review}
					userVoted={userVoted}
					openLoginModal={openLoginModal}
					incrementPoints={incrementPlusPoints}
					extraPoint={extraPlusPoint}
				/>
				<ReviewRatingItem
					sentimentType={SentimentType.negative}
					review={review}
					userVoted={userVoted}
					openLoginModal={openLoginModal}
					incrementPoints={incrementMinusPoints}
					extraPoint={extraMinusPoint}
				/>
			</div>
		</>
	)
}
